import type { Invoice, InvoiceLineItem } from 'payments-api/model';
import type { User } from 'payments-api/model/user';
import { useGetTransactions } from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { useMemo, useState } from 'react';
import { makePath } from 'utils/routes';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table/helpers';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { darkenHex, getRoleColorHex } from './constants';
import { DynamicInvoiceIcon } from './DynamicInvoiceIcon';
import { FormatBillingAmount } from './formatBillingAmount';
import { RoleColorDot } from './RoleColorDot';

export const InvoiceColumnHeaders = () => (
  <div className="grid grid-cols-6 gap-x-[1ch] px-f1 items-center h-f4 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-3">Invoice</div>
    <div className="col-span-2">Created</div>
    <div className="col-span-1 text-right">Amount</div>
  </div>
);

const PaymentColumnHeaders = () => (
  <div className="grid grid-cols-6 gap-x-[1ch] px-f1 items-center h-f4 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-3">Transaction</div>
    <div className="col-span-2">Invoice</div>
    <div className="col-span-1 text-right">Amount</div>
  </div>
);

// Helper to check if payout party matches a given externalId
const isPayoutPartyMatch = (
  item: InvoiceLineItem,
  externalId: string
): boolean => item.user_id === externalId;

type InvoiceRow = {
  id: string;
  created: string;
  totalForUser: bigint;
  iconColors: string[];
};

type PaymentRow = {
  id: string;
  externalId: string;
  invoiceId: string;
  invoiceIconColors: string[];
  amount: number;
  currencyCode: CurrencyCode;
  isPayin: boolean;
  date: string;
};

type UserViewProps = {
  user: User;
  users: User[];
  userInvoices: Invoice[];
  externalId: string;
  workspaceId: string;
};

// Format amount as currency
const formatCurrency = (amount: string | bigint): JSX.Element => {
  const amountNum =
    typeof amount === 'string' ? parseInt(amount, 10) : Number(amount);
  const formatted = (amountNum / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (
    <span className="inline-flex items-baseline gap-x-[1ch]">
      <span>$</span>
      <span>{formatted}</span>
    </span>
  );
};

/**
 * UserView component - Shared logic and UI for displaying user information
 * Used by UserDetailPage
 */
export const UserView = ({
  user,
  users,
  userInvoices,
  externalId,
  workspaceId,
}: UserViewProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<'invoices' | 'payments'>(
    'invoices'
  );

  const tabOptions = useMemo(
    () => [
      { label: 'Invoices', value: 'invoices' as const },
      { label: 'Payments', value: 'payments' as const },
    ],
    []
  );

  const { data: txData } = useGetTransactions();

  const userRoleMap = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u) => map.set(u.external_id, u.role));
    return map;
  }, [users]);

  // Prepare invoice data for SubTable
  const invoiceRows = useMemo(
    (): InvoiceRow[] =>
      userInvoices.map((invoice) => {
        // Check if this user is involved as a seller
        const userItemsInInvoice =
          invoice.line_items?.filter((item) =>
            isPayoutPartyMatch(item, externalId)
          ) || [];

        const totalForUser = userItemsInInvoice.reduce(
          (sum, item) => sum + BigInt(item.amount),
          BigInt(0)
        );

        const userIds = new Set(
          (invoice.line_items ?? []).map((li) => li.user_id)
        );
        const uniqueRoles = new Set(
          Array.from(userIds)
            .map((uid) => userRoleMap.get(uid))
            .filter((role): role is string => role !== undefined)
        );
        const colors = Array.from(uniqueRoles).map((role) =>
          getRoleColorHex(role)
        );
        if (colors.length === 1) {
          colors.push(darkenHex(colors[0]));
        }

        return {
          id: invoice.id,
          created: invoice.created,
          totalForUser,
          iconColors: colors,
        };
      }),
    [userInvoices, externalId, userRoleMap]
  );

  // Prepare payment rows from transaction allocations for this user
  const paymentRows = useMemo((): PaymentRow[] => {
    const allTransactions = (txData?.data ?? []);
    const rows: PaymentRow[] = [];
    allTransactions.forEach((txn) => {
      txn.allocations.forEach((alloc, i) => {
        const allocUserId = alloc.user.id;
        // Match on user id or externalId
        if (allocUserId !== externalId && allocUserId !== user.id) return;
        const invoiceForAlloc = userInvoices.find(
          (inv) => inv.id === alloc.invoice_id
        );
        const invoiceUserIds = new Set(
          (invoiceForAlloc?.line_items ?? []).map((li) => li.user_id)
        );
        const uniqueRoles = new Set(
          Array.from(invoiceUserIds)
            .map((uid) => userRoleMap.get(uid))
            .filter((role): role is string => role !== undefined)
        );
        const colors = Array.from(uniqueRoles).map((role) =>
          getRoleColorHex(role)
        );
        if (colors.length === 1) {
          colors.push(darkenHex(colors[0]));
        }
        rows.push({
          id: `${txn.id}-${i}`,
          externalId: txn.external_id,
          invoiceId: alloc.invoice_id,
          invoiceIconColors: colors,
          amount: Math.abs(Number(alloc.amount)),
          currencyCode: txn.currency as CurrencyCode,
          isPayin: alloc.type === 'invoice_payin',
          date: txn.modified ?? txn.posted,
        });
      });
    });
    return rows;
  }, [txData, externalId, user.id, userInvoices, userRoleMap]);

  // Define schema for invoices table
  const invoicesSchema: readonly ColumnDefinition<InvoiceRow>[] = useMemo(
    () => [
      {
        title: 'Invoice',
        colspan: 'col-span-3',
        // eslint-disable-next-line react/no-unstable-nested-components
        format: (row: InvoiceRow) => (
          <div className="flex items-center gap-x-[6px]">
            <DynamicInvoiceIcon colors={row.iconColors} height={14} />
            <span>{row.id}</span>
          </div>
        ),
      },
      {
        title: 'Created',
        colspan: 'col-span-2',
        format: (row: InvoiceRow) =>
          new Date(row.created).toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          }),
      },
      {
        title: 'Amount',
        justify: 'right' as const,
        colspan: 'col-span-1',
        // eslint-disable-next-line react/no-unstable-nested-components
        format: (row: InvoiceRow) => (
          <span className="ml-auto">{formatCurrency(row.totalForUser)}</span>
        ),
      },
    ],
    []
  );

  // Define schema for payments table
  const paymentsSchema: readonly ColumnDefinition<PaymentRow>[] = useMemo(
    () => [
      {
        title: 'Transaction',
        colspan: 'col-span-3',
        // eslint-disable-next-line react/no-unstable-nested-components
        format: (row: PaymentRow) => (
          <div className="flex items-center gap-x-[6px]">
            <span className="font-FragmentMono text-main-500">
              {row.isPayin ? '↙' : '↗'}
            </span>
            <span>{row.externalId}</span>
          </div>
        ),
      },
      {
        title: 'Invoice',
        colspan: 'col-span-2',
        // eslint-disable-next-line react/no-unstable-nested-components
        format: (row: PaymentRow) => (
          <div className="flex items-center gap-x-[6px]">
            <DynamicInvoiceIcon colors={row.invoiceIconColors} height={14} />
            <span>{row.invoiceId}</span>
          </div>
        ),
      },
      {
        title: 'Amount',
        justify: 'right' as const,
        colspan: 'col-span-1',
        // eslint-disable-next-line react/no-unstable-nested-components
        format: (row: PaymentRow) => (
          <span className="ml-auto">
            <FormatBillingAmount
              amount={row.amount}
              currencyCode={row.currencyCode}
              currencyPrecision={currencyPrecisionMap[row.currencyCode] || 2}
            />
          </span>
        ),
      },
    ],
    []
  );

  const getInvoicePath = (invoice: InvoiceRow): string =>
    makePath(['w', workspaceId, 'payments', 'invoices', invoice.id], {
      absolute: true,
    });

  const getPaymentPath = (row: PaymentRow): string =>
    makePath(['w', workspaceId, 'payments', 'invoices', row.invoiceId], {
      absolute: true,
    });
  const { totalPayouts, totalPayins } = useMemo(() => {
    let payouts = BigInt(0);
    let payins = BigInt(0);
    userInvoices.forEach((invoice) => {
      (invoice.line_items ?? [])
        .filter((item) => item.user_id === externalId)
        .forEach((item) => {
          if (item.type === InvoiceLineItemType.payout) {
            payouts += BigInt(item.amount);
          } else {
            payins += BigInt(item.amount);
          }
        });
    });
    return { totalPayouts: payouts, totalPayins: payins };
  }, [userInvoices, externalId]);

  return (
    <>
      {/* ID row: badge (circle + name) aligned to top of row */}
      <div className="flex items-start px-f1 py-f1 border-b-[0.5px] border-main-200 w-full h-f8">
        <div className="flex items-center gap-x-[0.25lh]">
          <RoleColorDot role={user.role} size={20} />
          <span className="text-lg text-main">{user.external_id}</span>
        </div>
      </div>

      {/* Totals rows */}
      <div className="flex items-center border-b-[0.5px] border-main-200 w-full">
        <div
          className={clsx(
            'px-f1 py-f1 text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0',
            kLabelColumnWidth
          )}
        >
          Total Payins
        </div>
        <div className="px-f1 py-f1 text-xmd text-main flex-1">
          {formatCurrency(totalPayins)}
        </div>
      </div>
      <div className="flex items-start h-f8 border-b-[0.5px] border-main-300 w-full">
        <div
          className={clsx(
            'px-f1 pt-f1 text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full',
            kLabelColumnWidth
          )}
        >
          Total Payouts
        </div>
        <div className="px-f1 pt-f1 text-xmd text-main flex-1">
          {formatCurrency(totalPayouts)}
        </div>
      </div>

      {/* Tabs and Content */}
      <div className="flex flex-col w-full min-h-f1">
        {/* Tab row with filler */}
        <div className="flex items-end w-full">
          <TabsV2
            options={tabOptions}
            selectedValue={activeTab}
            onClick={setActiveTab}
            data-testid="user-view-tabs"
            borderColor="main-300"
          />
          <div className="flex-1 border-b-[0.5px] border-main-300" />
        </div>

        {/* Column headers for invoices tab */}
        {activeTab === 'invoices' && <InvoiceColumnHeaders />}

        {/* Tab Content */}
        {activeTab === 'invoices' && (
          <NavTableV2
            data={invoiceRows}
            schema={invoicesSchema}
            getPath={getInvoicePath}
            textSize="xmd"
            showHeader={false}
            stickyHeader={false}
            rowHeight="fixed"
            emptyValue={{
              Invoice: (
                <span className="text-main-500">
                  No invoices found for this user
                </span>
              ),
              Created: '',
              Amount: '',
            }}
          />
        )}

        {activeTab === 'payments' && <PaymentColumnHeaders />}

        {activeTab === 'payments' && (
          <NavTableV2
            data={paymentRows}
            schema={paymentsSchema}
            getPath={getPaymentPath}
            textSize="xmd"
            showHeader={false}
            stickyHeader={false}
            rowHeight="fixed"
            emptyValue={{
              Transaction: (
                <span className="text-main-500">No payments yet</span>
              ),
              Invoice: '',
              Amount: '',
            }}
          />
        )}
      </div>
    </>
  );
};
